import { useEffect } from "react";
import { useViewStateWrapper } from "../../../../store/store";
import { analyticsGridKey } from "../../../../utils/helpers/analyticsGridKey";
import { getDrilldownColsKey } from "./drilldownColumns";

type DrilldownModalState = {
  open: boolean;
  facilityName: string;
  startDate: string;
  endDate: string;
  title: string;
  tableData: any;
  tableCols?: any[];
};

export const getDrilldownGridKey = (
  report: string,
  {
    facilityName,
    title,
    startDate,
    endDate,
  }: Pick<
    DrilldownModalState,
    "facilityName" | "title" | "startDate" | "endDate"
  >
) =>
  analyticsGridKey(
    "drilldown",
    report,
    facilityName,
    title,
    startDate,
    endDate
  );

// Mirrors an open drill-down table into the store; closing it clears the snapshot.
export const useDrilldownSnapshot = (
  report: string,
  {
    open,
    facilityName,
    startDate,
    endDate,
    title,
    tableData,
    tableCols,
  }: DrilldownModalState
) => {
  // Callers build tableCols inline, so key the effect on a cheap signature, not the array.
  const colsSignature = (tableCols ?? []).map((col) => col.accessor).join("|");
  useEffect(() => {
    if (!open) return;
    const colsKey = getDrilldownColsKey(tableCols);
    if (!colsKey) return;
    const rows = Array.isArray(tableData) ? tableData : [];
    const { drilldown, setDrilldown } = useViewStateWrapper.getState();
    const sameModal = drilldown?.report === report && drilldown.title === title;
    if (
      sameModal &&
      drilldown.tableData === rows &&
      drilldown.colsKey === colsKey
    ) {
      return;
    }
    setDrilldown({
      report,
      colsKey,
      facilityName,
      startDate,
      endDate,
      title,
      tableData: rows,
      viaIncident: sameModal ? drilldown.viaIncident : false,
    });
  }, [
    open,
    report,
    facilityName,
    startDate,
    endDate,
    title,
    tableData,
    colsSignature,
  ]);

  // Leaving without an incident click (Back, refresh, route change) drops the snapshot.
  useEffect(
    () => () => {
      const { drilldown, setDrilldown } = useViewStateWrapper.getState();
      if (drilldown && !drilldown.viaIncident) setDrilldown(null);
    },
    []
  );

  // Closing keeps the table's filters/sort/page so reopening it in the same report restores them.
  return () => useViewStateWrapper.getState().setDrilldown(null);
};
